import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { getMetrics } from "../../api/metrics";
import SectionHeading from "../../components/ui/SectionHeading";
import { fadeUp } from "../../utils/animations";

const staticPartners = [
  { name: "AWS",        src: "/logo/Amazon_Web_Services_Logo.svg" },
  { name: "Microsoft",  src: "/logo/Microsoft_logo.svg" },
  { name: "Cisco",      src: "/logo/Cisco.png" },
  { name: "Azure",      src: "/logo/Azure.png" },
  { name: "Red Hat",    src: "/logo/Redhat.png" },
  { name: "VMware",     src: "/logo/VMware.png" },
  { name: "CompTIA",    src: "/logo/CompTIA.png" },
  { name: "Kubernetes", src: "/logo/Kubernetes.png" },
  { name: "Linux",      src: "/logo/linux.png" },
  { name: "PMP",        src: "/logo/PMP.png" },
  { name: "PowerBI",    src: "/logo/PowerBI.png" },
  { name: "ITIL",       src: "/logo/ITIL.png" },
];

export default function TrustSection() {
  const { data: metrics } = useQuery({
    queryKey: ["metrics"],
    queryFn: () => getMetrics().then(r => r.data),
  });

  const metricEntries = Array.isArray(metrics)
    ? metrics.map((item, i) => ({
        key: item.id || i,
        value: item.value,
        label: item.label || item.description,
      }))
    : metrics
    ? Object.entries(metrics)
        .filter(([, val]) => typeof val === "string" || typeof val === "number")
        .map(([key, value]) => ({ key, value, label: key.replace(/_/g, " ") }))
    : [];

  return (
    <Box component="section" sx={{ bgcolor: "#F1F5F9", py: { xs: 10, md: 14 } }}>
      <div className="section-shell">
        <SectionHeading
          eyebrow="Trust and outcomes"
          title="Built on experience, certification credibility, and measurable training outcomes."
          copy="Trust signals built from real certification partnerships."
        />

        {/* Unified trust panel — metrics + partner credentials as one cohesive card */}
        <Box
          sx={{
            mt: 6,
            borderRadius: 4,
            bgcolor: "white",
            border: "1px solid rgba(15,23,42,0.06)",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 16px 40px rgba(15,23,42,0.06)",
            overflow: "hidden",
          }}
        >
          {/* Metrics stat bar */}
          {metricEntries.length > 0 && (
            <Box
              sx={{
                px: { xs: 3, md: 5 },
                py: { xs: 4, md: 5 },
                background: "linear-gradient(135deg, #0B3FA5 0%, #1d4ed8 55%, #2A8DFF 100%)",
              }}
            >
             <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: { xs: 3, md: 6 },
                  width: "100%",
                }}
              >
                {metricEntries.map((m, i) => (
                  <Box key={m.key} sx={{ minWidth: { xs: "40%", sm: "auto" } }}>
                    <motion.div {...fadeUp(i * 0.05)}>
                      <Box
                        sx={{
                          textAlign: "center",
                          borderRight: {
                            sm: i < metricEntries.length - 1 ? "1px solid rgba(255,255,255,0.16)" : "none",
                          },
                          px: { sm: 3 },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "1.9rem", md: "2.4rem" },
                            fontWeight: 800,
                            color: "white",
                            lineHeight: 1.1,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {m.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.78)",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        >
                          {m.label}
                        </Typography>
                      </Box>
              </motion.div>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Partner logo strip */}
          <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
           <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 3,
                textAlign: "center",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "text.secondary",
                fontSize: "0.7rem",
              }}
            >
              Certification &amp; technology partners
            </Typography>

          <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: { xs: 2, md: 3 },
              }}
            >
              {staticPartners.map(({ name, src }) => (
                <Box key={name} sx={{ width: { xs: "26%", sm: 110 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 2.5,
                      px: 1.5,
                      borderRadius: 2.5,
                      transition: "background-color 0.2s ease, transform 0.2s ease",
                      "&:hover": {
                        bgcolor: "#F8FAFC",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={src}
                      alt={name}
                      sx={{
                        height: 32,
                        width: "auto",
                        maxWidth: "100%",
                        objectFit: "contain",
                        filter: "grayscale(1) opacity(0.65)",
                        transition: "filter 0.2s ease",
                        ".MuiBox-root:hover &": { filter: "grayscale(0) opacity(1)" },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.68rem" }}
                    >
                      {name}
                    </Typography>
                </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </div>
    </Box>
  );
}